import { Button, ConfigProvider, Space, theme as antdTheme } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { radius } from '../../theme/radius';

const PRIVACY_POLICY_URLS: Record<string, string> = {
  fr: 'https://www.libertify.com/fr/privacy-policy/',
  // Redirects to the English page until a Spanish one is published
  es: 'https://www.libertify.com/es/privacy-policy/',
  en: 'https://www.libertify.com/privacy-policy/',
};

const getPrivacyPolicyUrl = (language: string | undefined): string => {
  const lang = language?.split('-')[0] ?? 'en';
  return PRIVACY_POLICY_URLS[lang] ?? PRIVACY_POLICY_URLS['en'] ?? '';
};

export type CookieDisclaimerProps = {
  isOpen: boolean;
  onAccept: () => void;
  onReject: () => void;
};

export const CookieDisclaimer = ({
  isOpen,
  onAccept,
  onReject,
}: CookieDisclaimerProps) => {
  const { t, i18n } = useTranslation(undefined, { useSuspense: false });

  const handleLearnMore = () => {
    globalThis.open(getPrivacyPolicyUrl(i18n.language), '_blank');
  };

  if (!isOpen) return null;

  // Own ConfigProvider so a customer theme on the host page doesn't restyle the banner
  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: colors.primary[500],
          fontFamily: typography.fontFamily.primary,
        },
      }}
    >
      <StyledDialog open aria-labelledby="cookie-disclaimer-title">
        <StyledTitle id="cookie-disclaimer-title">
          {t('cookieDisclaimer.title')}
        </StyledTitle>

        <StyledDescription>
          {t('cookieDisclaimer.description')}
        </StyledDescription>

        {/* Same style for both choices, so rejecting is as easy as accepting */}
        <Space size={8}>
          <Button type="primary" size="small" onClick={onReject}>
            {t('cookieDisclaimer.reject')}
          </Button>
          <Button type="primary" size="small" onClick={onAccept}>
            {t('cookieDisclaimer.accept')}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={handleLearnMore}
            style={{ padding: 0, color: colors.primary[500] }}
          >
            {t('cookieDisclaimer.learnMore')}
          </Button>
        </Space>
      </StyledDialog>
    </ConfigProvider>
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

const StyledDialog = styled.dialog`
  position: fixed;
  inset: auto 24px 12px auto;
  margin: 0;
  width: 340px;
  background-color: ${colors.neutral.white};
  border-radius: ${radius.box}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  z-index: 9999;

  @media (width <= 480px) {
    left: 16px;
    right: 16px;
    bottom: 16px;
    width: auto;
  }
`;

const StyledTitle = styled.span`
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 500;
`;

const StyledDescription = styled.p`
  margin-bottom: 12px;
  font-size: 13px;
  color: ${colors.neutral[800]};
`;
